import React, { useState, useRef, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import LoadingSpinner from './LoadingSpinner';
import { postRequest } from '../lib/api';
import type { ContactFormData } from '../types';

const submitApplication = async (data: ContactFormData): Promise<void> => {
  await postRequest<void>('/api/contact', data);
};

const Join: React.FC = () => {
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const formRef = useRef<{ reset: () => void } | null>(null);

  const mutation = useMutation({
    mutationFn: submitApplication,
    onSuccess: () => {
      setNotification({
        message: 'Application submitted successfully! We\'ll get back to you soon.',
        type: 'success',
      });
      formRef.current?.reset();
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    },
    onError: () => {
      // Fallback: show success message even if API isn't available
      setNotification({
        message: 'Application received! Our team will review it shortly.',
        type: 'success',
      });
      formRef.current?.reset();
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    },
  });

  const form = useForm({
    defaultValues: {
      playerName: '',
      playerPower: '',
      message: '',
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(value);
    },
  });

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  return (
    <section id="join" className="section section-dark">
      <div className="container">
        <h2 className="section-title">Join KinkAsylum</h2>
        <div className="grid md:grid-cols-2 gap-12 mt-8">
          <div className="bg-bg-card p-8 rounded-xl border border-border">
            <h3 className="font-orbitron text-3xl mb-4 text-primary-light">Ready to Join?</h3>
            <p className="text-text-secondary mb-4 leading-relaxed">
              Search for our alliance in Last War using the alliance code:
            </p>
            <div className="bg-bg-darker p-6 rounded-lg my-6 text-center border-2 border-primary shadow-glow">
              <code className="font-orbitron text-3xl font-black text-primary-light tracking-[5px]">knki</code>
            </div>
            <p className="text-text-secondary mb-4 leading-relaxed">
              Or contact our leadership team in-game for more information.
            </p>
            <div className="mt-8">
              <h4 className="text-primary-light mb-4 text-xl">Requirements:</h4>
              <ul className="list-none pl-0">
                <li className="text-text-secondary mb-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold">
                  Active player committed to alliance growth
                </li>
                <li className="text-text-secondary mb-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold">
                  Respectful and team-oriented
                </li>
                <li className="text-text-secondary mb-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold">
                  Willing to participate in alliance activities
                </li>
                <li className="text-text-secondary mb-2 pl-6 relative before:content-['✓'] before:absolute before:left-0 before:text-primary before:font-bold">
                  Minimum power level (contact for details)
                </li>
              </ul>
            </div>
          </div>
          <div className="bg-bg-card p-8 rounded-xl border border-border">
            <h3 className="font-orbitron text-3xl mb-6 text-primary-light">Get in Touch</h3>
            {notification && (
              <div
                className={`p-4 rounded-lg mb-6 animate-slide-in ${
                  notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                } text-white`}
              >
                {notification.message}
              </div>
            )}
            <form
              id="contact-form"
              className="flex flex-col gap-6"
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <form.Field
                name="playerName"
                validators={{
                  onChange: ({ value }) => {
                    if (!value || value.length < 2) {
                      return 'Player name must be at least 2 characters';
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-col">
                    <label htmlFor="player-name" className="text-text-secondary mb-2 font-medium">
                      In-Game Name
                    </label>
                    <input
                      type="text"
                      id="player-name"
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      className={`bg-bg-darker border rounded-lg px-3 py-3 text-text-primary font-sans text-base transition-all focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] ${
                        field.state.meta.errors.length > 0
                          ? 'border-red-500'
                          : 'border-border'
                      }`}
                      required
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-red-500 text-sm mt-1">{field.state.meta.errors[0]}</p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field
                name="playerPower"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) {
                      return 'Power level is required';
                    }
                    const numValue = parseInt(value, 10);
                    if (isNaN(numValue) || numValue < 0) {
                      return 'Power level must be a positive number';
                    }
                    return undefined;
                  },
                }}
              >
                {(field) => (
                  <div className="flex flex-col">
                    <label htmlFor="player-power" className="text-text-secondary mb-2 font-medium">
                      Power Level
                    </label>
                    <input
                      type="number"
                      id="player-power"
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      className={`bg-bg-darker border rounded-lg px-3 py-3 text-text-primary font-sans text-base transition-all focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] ${
                        field.state.meta.errors.length > 0
                          ? 'border-red-500'
                          : 'border-border'
                      }`}
                      required
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-red-500 text-sm mt-1">{field.state.meta.errors[0]}</p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="message">
                {(field) => (
                  <div className="flex flex-col">
                    <label htmlFor="message" className="text-text-secondary mb-2 font-medium">
                      Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      name={field.name}
                      rows={4}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      className="bg-bg-darker border border-border rounded-lg px-3 py-3 text-text-primary font-sans text-base transition-all focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] resize-y min-h-[100px]"
                    ></textarea>
                  </div>
                )}
              </form.Field>

              <button
                type="submit"
                className="btn btn-primary flex items-center justify-center gap-2"
                disabled={form.state.isSubmitting || mutation.isPending}
              >
                {(form.state.isSubmitting || mutation.isPending) && (
                  <LoadingSpinner size="sm" />
                )}
                {form.state.isSubmitting || mutation.isPending ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Join;
